import { Bot, HelpCircle, Calendar, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { toast } from 'lib/toast-unified';

interface HomeTabProps {
  onTabChange: (tab: string) => void;
}

// Utility to get greeting based on time of day
const getGreeting = (): string => {
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 12) {
    return 'Bom dia';
  } else if (hour >= 12 && hour < 18) {
    return 'Boa tarde';
  } else {
    return 'Boa noite';
  }
};

// Mock recent activity data
const MOCK_RECENT_ACTIVITIES = [
  {
    id: 1,
    title: 'Reunião agendada',
    description: 'Reunião de planejamento às 14:00',
    time: '2 horas atrás',
    icon: Calendar,
  },
  {
    id: 2,
    title: 'Curso concluído',
    description: 'Módulo de Automação finalizado',
    time: '1 dia atrás',
    icon: Clock,
  },
  {
    id: 3,
    title: 'Novo formulário criado',
    description: 'Formulário de feedback publicado',
    time: '3 dias atrás',
    icon: Clock,
  },
];

const HomeTab = ({ onTabChange }: HomeTabProps) => {
  const navigate = useNavigate();
  const greeting = getGreeting();

  // Handle AI chat action - show "coming soon" toast since tab was removed
  const handleAIChatAction = () => {
    toast.info('Em breve', {
      description: 'O assistente de IA estara disponivel em breve. Aguarde novidades!',
    });
  };

  // Quick action cards configuration
  const quickActions = [
    {
      id: 'ai-chat',
      title: 'Falar com IA',
      description: 'Converse com nosso assistente inteligente',
      icon: Bot,
      action: handleAIChatAction,
    },
    {
      id: 'support',
      title: 'Abrir Suporte',
      description: 'Acesse tutoriais e documentacao',
      icon: HelpCircle,
      action: () => onTabChange('support'),
    },
    {
      id: 'agenda',
      title: 'Minha Agenda',
      description: 'Visualize compromissos',
      icon: Calendar,
      action: () => navigate('/cockpit'),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header with Greeting */}
      <div className="space-y-2">
        <h3 className="text-2xl font-bold">{greeting}!</h3>
        <p className="text-sm text-muted-foreground">
          Como podemos ajudá-lo hoje? Escolha uma das opções abaixo.
        </p>
      </div>

      {/* Quick Action Cards */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Ações Rápidas
        </h4>
        <div className="grid grid-cols-1 gap-2">
          {quickActions.map(action => {
            const Icon = action.icon;
            return (
              <Button
                key={action.id}
                variant="outline"
                className="justify-start h-auto p-4 border border-primary/20 bg-background hover:bg-primary/5 hover:border-primary/40 hover:scale-[1.02] transition-all duration-200"
                onClick={action.action}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div className="text-left flex-1">
                  <div className="font-medium text-sm">{action.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{action.description}</div>
                </div>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Atividade Recente
          </h4>
          <Badge variant="secondary" className="text-xs">
            Dados simulados
          </Badge>
        </div>
        <div className="space-y-2">
          {MOCK_RECENT_ACTIVITIES.map(activity => {
            const Icon = activity.icon;
            return (
              <Card key={activity.id} className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{activity.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {activity.description}
                      </p>
                      <p className="text-xs text-muted-foreground/70 mt-1">{activity.time}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Bottom Hint */}
      <div className="pt-4 border-t">
        <p className="text-xs text-center text-muted-foreground">
          Navegue pelas abas abaixo para acessar mais recursos
        </p>
      </div>
    </div>
  );
};

export default HomeTab;
